import moment from "moment";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import Swal from "sweetalert2";
import { ReactComponent as WhatsAppIcon } from "../../../assets/newImages/icons/whatsapp.svg";
import { Button } from "../../../components/Button";
import PopoverInfo from "../../../components/PopoverInfo/PopoverInfo";
import { FooterPaginas } from "../../../components/FooterPaginas/FooterPaginas";
import ModalLayoutCsv from "../../../components/ModalLayoutCsv/ModalLayoutCsv";
import ModalLayoutImpressao from "../../../components/ModalLayoutImpressao/ModalLayoutImpressao";
import CodigoRastreio from "../../../components/Pedido/CodigoRastreio/CodigoRastreio";
import EditarDadosCliente from "../../../components/Pedido/EditarDadosCliente/EditarDadosCliente";
import NotaFiscal from "../../../components/Pedido/NotaFiscal/NotaFiscal";
import ObservacoesPedido from "../../../components/Pedido/ObservacoesPedido/ObservacoesPedido";
import PrintablePedido from "../../../components/PrintablePedido/PrintablePedido";
import { SelectPedidos } from "../../../components/Select/SelectPedidos";
import { SkeletonPedidoCesta } from "../../../components/Skeletons/SkeletonPedidoCesta/SkeletonPedidoCesta";
import TableProdutos from "../../../components/TableProdutos/TableProdutos";
import { carregarOrdenacao, OrdenacaoConfig } from "../../../components/TableProdutos/OrdenacaoButton/ordenacaoUtils";
import { EmpresaContext } from "../../../contexts/Empresa/EmpresaContext";
import { useTheme } from "../../../contexts/Theme/ThemeContext";
import { MelhorEnvioApi } from "../../../hooks/melhorEnvio";
import { PedidosApi } from "../../../hooks/pedidosApi";
import { cepMask, cnpjMask, cpfMask, phoneMask } from "../../../services/Masks";
import ObservacaoPedido from "../../../types/ObservacaoPedido";
import Pagamento from "../../../types/Pagamento";
import PedidoUnico from "../../../types/PedidoUnico";
import { LayoutImpressaoPedido, defaultLayoutPedido } from "../../../types/LayoutImpressao";
import { camposAtivosCsvPedido } from "../../../types/LayoutCsv";
import { ContainerPedido } from "./styles";

interface PedidoProps {}

const Pedido: React.FC<PedidoProps> = ({}) => {
  const { theme } = useTheme();
  const pedidosApi = PedidosApi();
  const melhorEnvioApi = MelhorEnvioApi();
  const { empresa, setEmpresa } = useContext(EmpresaContext);
  const { id } = useParams<{ id: string }>();
  const [codigoRastreio, setCodigoRastreio] = useState("");
  const [loadingPrinting, setLoadingPrinting] = useState(false);
  const printableRef = useRef<HTMLDivElement>(null);
  const [modalLayoutOpen, setModalLayoutOpen] = useState(false);
  const [modalCsvOpen, setModalCsvOpen] = useState(false);
  const [layoutConfig, setLayoutConfig] = useState<LayoutImpressaoPedido>(() => {
    const stored = localStorage.getItem("layoutImpressaoPedidos");
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return defaultLayoutPedido;
      }
    }
    return defaultLayoutPedido;
  });
  const [ordenacaoProdutos, setOrdenacaoProdutos] = useState<OrdenacaoConfig>(carregarOrdenacao);
  const meiosDePagamento = {
    pix: "Pix",
    cartao: "Cartão",
    a_combinar: "A combinar",
  };
  const intermediadores = {
    pagseguro: "PagSeguro",
    pagarme: "Pagarme",
  };

  const [pedido, setPedido] = useState<PedidoUnico>();
  const [observacoes, setObservacoes] = useState<ObservacaoPedido[]>([]);
  const [loadingForcar, setLoadingForcar] = useState(false);
  const [informacoesPagamento, setInformacoesPagamento] = useState<
    Pagamento | undefined
  >();
  const [situacoes, setSituacoes] = useState<
    Array<{ value: number; name: string; quantidade?: number }>
  >([]);
  const [loadingSkeleton, setLoadingSkeleton] = useState(true);
  const [loading, setLoading] = useState(false);

  const cores = {
    // cores conforme o id da situação no banco
    1: "#FFBE9D",
    7: "#E7BE00",
    8: "#4DE889",
    4: "#00A2BB",
    5: "#0093FB",
    6: "#FF6E28",
    2: "#0D5229",
    3: "#9BA9FB",
    9: "#FF0000",
  };

  const init = async () => {
    if (!id) return;
    const [pedido, situacoes, informacoesPagamento, observacoesPedido] = await Promise.all([
      pedidosApi.getPedido(id),
      pedidosApi.getSituacoes(),
      pedidosApi.getInformacoesPagamento(id),
      pedidosApi.getObservacoesPedido(id),
    ]);
    setPedido(pedido);

    setSituacoes(
      situacoes.map((situacao: any) => ({
        name: situacao.descricao,
        value: situacao.id.toString(),
      }))
    );
    setInformacoesPagamento(informacoesPagamento);
    setObservacoes(observacoesPedido || []);

    setCodigoRastreio(pedido.frete.codigo_rastreio || "");

    setLoadingSkeleton(false);
  };

  const handleForcarSincronizacao = async () => {
    if (!id) return;
    setLoadingForcar(true);
    try {
      await pedidosApi.forcarSincronizacaoPedido(id);
      await init();
      Swal.fire({
        icon: "success",
        title: "Pedido reenfileirado!",
        text: "O pedido será reprocessado na próxima sincronização.",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Erro!",
        text: "Não foi possível forçar o pedido.",
        showConfirmButton: false,
        timer: 1500,
      });
    } finally {
      setLoadingForcar(false);
    }
  };


  const handleSituacaoChange = async (situacao: string) => {
    if (!id || pedido?.situacao === situacao) return;
    if (situacao === "3") {
      handleCancelarPedido();
    } else {
      setLoading(true);
      await pedidosApi.alterarSituacao([id], Number.parseInt(situacao)).then(
        (res) => {
          Swal.fire({
            icon: "success",
            showConfirmButton: false,
            timer: 1000,
            title: "Situação do pedido alterada com sucesso!",
          });
          setPedido({ ...pedido!, situacao: situacao });
          setLoading(false);
        },
        (err) => {
          Swal.fire({
            icon: "error",
            showConfirmButton: false,
            timer: 1500,
            title: "Erro ao alterar situação do pedido!",
          });
          setLoading(false);
        }
      );
    }
  };

  const handleExportarPedido = async () => {
    if (!id) return;
    pedidosApi
      .exportarPedidos([id], camposAtivosCsvPedido())
      .catch((err) => {
        Swal.fire({
          icon: "error",
          showConfirmButton: false,
          timer: 1500,
          title: "Erro ao exportar pedido!",
        });
      });
  };

  const handleCancelarPedido = async () => {
    if (!id) return;
    // Cancelado
    Swal.fire({
      title: "Tem certeza que deseja cancelar este pedido?",
      text: "Esta ação não poderá ser desfeita e irá repor os estoques dos produtos caso você utilize.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Cancelar pedido",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      setLoading(true);
      if (result.isConfirmed) {
        pedidosApi.alterarSituacao([id], Number.parseInt("3")).then(
          (res) => {
            setPedido({
              ...pedido!,
              situacao: "3",
              situacao_descricao: "Cancelado",
            });
            Swal.fire({
              icon: "success",
              showConfirmButton: false,
              timer: 1500,
              title: "Pedido cancelado com sucesso!",
            });
            setLoading(false);
          },
          (err) => {
            Swal.fire({
              icon: "error",
              showConfirmButton: false,
              timer: 1500,
              title: "Erro ao cancelar pedido!",
            });
            setLoading(false);
          }
        );
      } else {
        setLoading(false);
      }
    });
  };

  const changeFretes = (codigo: string, url_etiqueta) => {
    setPedido({
      ...pedido!,
      frete: { ...pedido?.frete, codigo_rastreio: codigo, url_etiqueta } as any,
    });
  };

  const handlePrint = useReactToPrint({
    content: () => printableRef.current,
    onBeforeGetContent: async () => {
      setOrdenacaoProdutos(carregarOrdenacao());
      setLoadingPrinting(true);
    },
    onAfterPrint: () => setLoadingPrinting(false),
    onPrintError: () => {
      Swal.fire({
        icon: "error",
        showConfirmButton: false,
        timer: 1500,
        title: "Erro ao imprimir pedido!",
      });
      setLoadingPrinting(false);
    },
  });

  const handleModalClose = () => {
    setModalLayoutOpen(false);
    const stored = localStorage.getItem("layoutImpressaoPedidos");
    if (stored) {
      try {
        setLayoutConfig(JSON.parse(stored));
      } catch {
        setLayoutConfig(defaultLayoutPedido);
      }
    }
  };

  const memoizedPedido = useMemo(() => pedido, [pedido]);
  const memoizedInformacoesPagamento = useMemo(
    () => informacoesPagamento,
    [informacoesPagamento]
  );

  useEffect(() => {
    init();
  }, []);

  const switchFormasDePagamento = () => {
    switch (informacoesPagamento?.tipo) {
      case "cartao":
        return (
          <>
            {informacoesPagamento?.intermediador ||
            informacoesPagamento?.tipo ? (
              <div className="column-container">
                <div className="titulo-pedido fonteTexto">Forma de pagamento</div>
                <div className="subtitulo-pedido fonteSubtitulo">
                  {intermediadores[informacoesPagamento.intermediador] || ""},{" "}
                  {meiosDePagamento[informacoesPagamento.tipo] || ""}
                </div>
              </div>
            ) : null}
            {informacoesPagamento?.cartao && informacoesPagamento?.parcelas ? (
              <div className="column-container">
                <div className="titulo-pedido fonteTexto">Parcelas</div>
                <div className="subtitulo-pedido fonteSubtitulo">
                  {`${
                    informacoesPagamento.cartao[0].toUpperCase() +
                    informacoesPagamento.cartao.slice(1).toLowerCase()
                    } x${informacoesPagamento.parcelas}`}{`${Number(informacoesPagamento?.juros_valor) > 0 ? " com juros" : ""}`}
                </div>
              </div>
            ) : null}
            {informacoesPagamento?.id_transacao ? (
              <div className="column-container">
                <div className="titulo-pedido fonteTexto">ID da transação</div>
                <div className="subtitulo-pedido fonteSubtitulo">
                  {informacoesPagamento.id_transacao}
                </div>
              </div>
            ) : null}
          </>
        );
      case "pix":
        return (
          <>
            {informacoesPagamento?.intermediador ||
            informacoesPagamento?.tipo ? (
              <div className="column-container">
                <div className="titulo-pedido fonteTexto">Forma de pagamento</div>
                <div className="subtitulo-pedido fonteSubtitulo">
                  {intermediadores[informacoesPagamento.intermediador] || ""},{" "}
                  {meiosDePagamento[informacoesPagamento.tipo] || ""}
                </div>
              </div>
            ) : null}
            {informacoesPagamento?.id_transacao ? (
              <div className="column-container">
                <div className="titulo-pedido fonteTexto">ID da transação</div>
                <div className="subtitulo-pedido fonteSubtitulo">
                  {informacoesPagamento.id_transacao}
                </div>
              </div>
            ) : null}
            {informacoesPagamento?.pix_code ? (
              <div className="column-container">
                <div className="titulo-pedido fonteTexto">Enviar chave pix</div>
                <div className="pix-div">
                  <Button
                    className="botao"
                    padding="5rem"
                    onClick={() =>
                      navigator.clipboard.writeText(
                        informacoesPagamento.pix_code
                      )
                    }
                    title={"Copiar chave"}
                  />
                  <a
                    target="_blank"
                    href={`https://api.whatsapp.com/send?phone=55${pedido?.cliente.telefone}&text=${informacoesPagamento.pix_code}`}
                  >
                    <img
                      className="icone"
                      src={
                        require("../../../assets/newImages/icons/whatsapp.svg")
                          .default
                      }
                      alt=""
                    />
                  </a>
                  <a
                    target="_blank"
                    href={`https://telegram.me/share/url?url=ht&text=${informacoesPagamento.pix_code}`}
                  >
                    <img
                      className="icone"
                      src={
                        require("../../../assets/newImages/icons/telegram-alt.svg")
                          .default
                      }
                      alt=""
                    />
                  </a>
                </div>
              </div>
            ) : null}
          </>
        );
        break;
      case "a_combinar":
        return (
          <>
            {informacoesPagamento?.tipo ? (
              <div className="column-container">
                <div className="titulo-pedido fonteTexto">Forma de pagamento</div>
                <div className="subtitulo-pedido fonteSubtitulo">
                  {meiosDePagamento[informacoesPagamento.tipo] || ""}
                </div>
              </div>
            ) : null}
          </>
        );

      default:
        return <></>;
        break;
    }
  };

  const formatarTipoFrete = (frete: string | undefined) => {
    if (!frete) return "Não informado";

    const fretesLabels = {
      entrega_local: "Entrega local",
      frete_gratis: "Frete grátis",
      retirada_no_local: "Retirada no local",
      a_combinar: "A combinar",
    };

    frete.startsWith("especial_")
      ? (frete = `Especial - ${frete.replace("especial_", "")}`)
      : (frete = fretesLabels[frete]
          ? fretesLabels[frete]
          : frete.toUpperCase());

    return frete;
  };

  const calcularDescontoProgressivo = () => {
    return pedido?.desconto_progressivo?.valor || 0;
  };

  const calcularDescontoCupom = () => {
    if (!pedido?.desconto_cupom || !pedido?.valor_total) return 0;

    const desconto = Number.parseFloat(pedido.desconto_cupom.toString());
    const valorTotal = Number.parseFloat(pedido.valor_total.toString());
    const valorAposProgressivo = valorTotal - calcularDescontoProgressivo();

    if (pedido.tipo_desconto === "porcentagem") {
      return (desconto / 100) * valorAposProgressivo;
    } else {
      return desconto;
    }
  };

  const calcularDescontoPix = () => {
    if (!informacoesPagamento?.desconto) return 0;

    const percentualPix = Number.parseFloat(informacoesPagamento.desconto) / 100;
    const valorTotal = Number.parseFloat(pedido?.valor_total || "0");
    const descontoCupomReais = calcularDescontoCupom() / 100;
    const valorAposCupom = valorTotal - calcularDescontoProgressivo() - descontoCupomReais;

    return percentualPix * valorAposCupom;
  };

  return (
    <>
      {loadingSkeleton ? (
        <SkeletonPedidoCesta />
      ) : (
        <ContainerPedido theme={theme}>
          <div className="flex-container flex-titulo">
            <div className="subtitulo-pedido fonteTitulo titulo-com-badge">
              <span>Pedido #{pedido?.id_pedido_sequencial || "????"}</span>
              {pedido?.dropshipping?.recebido && (
                <span className="badge-dropshipping fonteRotulos">Dropshipping</span>
              )}
            </div>
            {pedido?.situacao === "3" || pedido?.dropshipping?.travado ? (
              <div className="subtitulo-pedido fonteSubtitulo">
                <div
                  className="bolinha"
                  style={{
                    backgroundColor:
                      cores[Number.parseInt(pedido?.situacao!) || 0],
                  }}
                />
                {pedido?.situacao_descricao || ""}
              </div>
            ) : (
              <span className="select-pedido-unico">
                <SelectPedidos
                  options={
                    pedido?.situacao ===
                    situacoes
                      .find((situacao) => situacao.name === "Excluído")
                      ?.value.toString()
                      ? situacoes
                      : situacoes.filter(
                          (situacao) => situacao.name !== "Excluído"
                        )
                  }
                  loading={loading}
                  value={pedido?.situacao?.toString()}
                  onChange={handleSituacaoChange}
                  cores={cores}
                />
              </span>
            )}
          </div>
          <div className="linha"></div>
          <div className="flex-container flex-titulo">
            <div className="titulo-pedido fonteRotulos">
              Data: {moment(pedido?.data).format("DD/MM/YYYY HH:mm:ss")}
            </div>
            <div className="titulo-pedido fonteRotulos">Tabela: {pedido?.tabela || ""}</div>
          </div>
          <div className="container-branco">
            <div className="flex-container">
              {pedido?.cliente.nome || pedido?.cliente.razao_social ? (
                <div className="column-container">
                  <div className="titulo-pedido fonteTexto">Cliente</div>
                  <div className="subtitulo-pedido fonteSubtitulo">
                    <span>
                      {pedido?.cliente.nome || pedido?.cliente.razao_social}
                    </span>
                  </div>
                </div>
              ) : null}

              {pedido?.cliente.telefone ? (
                <div className="column-container">
                  <div className="titulo-pedido fonteTexto">Telefone</div>
                  <div className="subtitulo-pedido fonteSubtitulo">
                    <a
                      target="blank"
                      href={`https://api.whatsapp.com/send?phone=55${pedido?.cliente.telefone}`}
                    >
                      {phoneMask(pedido?.cliente.telefone)}
                    </a>
                  </div>
                </div>
              ) : null}

              {pedido?.cliente.email ? (
                <div className="column-container">
                  <div className="titulo-pedido fonteTexto">Email</div>
                  <div className="subtitulo-pedido fonteSubtitulo">
                    <a href={`mailto:${pedido?.cliente.email}`} target="_blank">
                      {pedido?.cliente.email}
                    </a>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="flex-container">
              {pedido?.cliente.cpf || pedido?.cliente.cnpj ? (
                <div className="column-container">
                  <div className="titulo-pedido fonteTexto">CPF/CNPJ</div>
                  <div className="subtitulo-pedido fonteSubtitulo">
                    {cpfMask(pedido?.cliente.cpf || "") ||
                      cnpjMask(pedido?.cliente.cnpj || "")}
                  </div>
                </div>
              ) : null}

              {pedido?.cliente.inscricao_estadual ? (
                <div className="column-container">
                  <div className="titulo-pedido fonteTexto">Inscrição Estadual</div>
                  <div className="subtitulo-pedido fonteSubtitulo">
                    {pedido?.cliente.inscricao_estadual}
                  </div>
                </div>
              ) : null}

              {pedido?.cliente.data_nascimento ? (
                <div className="column-container">
                  <div className="titulo-pedido fonteTexto">Data de nascimento</div>
                  <div className="subtitulo-pedido fonteSubtitulo">
                    {moment(pedido?.cliente.data_nascimento).format(
                      "DD/MM/YYYY"
                    )}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="linha-menor"></div>

            <div className="flex-container">
              {pedido?.cliente.cep ? (
                <div className="column-container">
                  <div className="titulo-pedido fonteTexto">CEP</div>
                  <div className="subtitulo-pedido fonteSubtitulo">
                    {cepMask(pedido?.cliente.cep)}
                  </div>
                </div>
              ) : null}
              {pedido?.cliente.cidade && pedido?.cliente.estado ? (
                <div className="column-container">
                  <div className="titulo-pedido fonteTexto">Cidade</div>
                  <div className="subtitulo-pedido fonteSubtitulo">
                    {pedido?.cliente.cidade} / {pedido?.cliente.estado}
                  </div>
                </div>
              ) : null}
              {pedido?.cliente.bairro ? (
                <div className="column-container">
                  <div className="titulo-pedido fonteTexto">Bairro</div>
                  <div className="subtitulo-pedido fonteSubtitulo">
                    {pedido?.cliente.bairro}
                  </div>
                </div>
              ) : null}

              {pedido?.cliente.endereco || pedido?.cliente.numero ? (
                <div className="column-container">
                  <div className="titulo-pedido fonteTexto">Endereço</div>
                  <div className="subtitulo-pedido fonteSubtitulo">
                    {pedido?.cliente.endereco}, {pedido?.cliente.numero}
                    {pedido?.cliente.complemento
                      ? `, ${pedido?.cliente.complemento}`
                      : null}
                  </div>
                </div>
              ) : null}
            </div>
            {pedido?.erp_situacao === -1 && pedido?.cliente && (
              <div className="flex-container" style={{ marginTop: "2rem" }}>
                <EditarDadosCliente
                  cliente={pedido.cliente}
                  id_pedido={id}
                  onSaved={init}
                />
              </div>
            )}
          </div>

          {/* Dropshipping: origem da venda no lugar de catálogo/vendedor */}
          {pedido?.dropshipping?.recebido ? (
            <div className="container-branco">
              <div className="flex-container">
                <div className="column-container">
                  <div className="titulo-pedido fonteTexto">Revendedor</div>
                  <div className="subtitulo-pedido fonteSubtitulo">
                    {pedido.dropshipping.origem?.satelite || "-"}
                  </div>
                </div>
                <div className="column-container">
                  <div className="titulo-pedido fonteTexto">Nome do revendedor</div>
                  <div className="subtitulo-pedido fonteSubtitulo">
                    {pedido.dropshipping.origem?.chave || pedido.dropshipping.revendedora || "-"}
                  </div>
                </div>
                <div className="column-container">
                  <div className="titulo-pedido fonteTexto">Catálogo da venda</div>
                  <div className="subtitulo-pedido fonteSubtitulo">
                    {pedido.dropshipping.origem?.catalogo_satelite || "-"}
                  </div>
                </div>
                <div className="column-container">
                  <div className="titulo-pedido fonteTexto">Catálogo liberado</div>
                  <div className="subtitulo-pedido fonteSubtitulo">
                    {pedido.dropshipping.origem?.catalogo_mae || "-"}
                  </div>
                </div>
                <div className="column-container">
                  <div className="titulo-pedido fonteTexto">Pedido de origem</div>
                  <div className="subtitulo-pedido fonteSubtitulo">
                    {pedido.dropshipping.origem?.id_pedido_satelite
                      ? `#${pedido.dropshipping.origem.id_pedido_satelite} em ${moment(
                          pedido.dropshipping.origem.data_pedido_satelite
                        ).format("DD/MM/YYYY HH:mm:ss")}`
                      : "-"}
                  </div>
                </div>
              </div>
            </div>
          ) : (
          <div className="container-branco">
            <div className="flex-container">
              {pedido?.catalogo?.descricao ? (
                <div className="column-container">
                  <div className="titulo-pedido fonteTexto">Catálogo</div>
                  <div className="subtitulo-pedido fonteSubtitulo">
                    {pedido?.catalogo.descricao}
                  </div>
                </div>
              ) : null}
              {pedido?.nome ? (
                <div className="column-container">
                  <div className="titulo-pedido fonteTexto">Vendedor</div>
                  <div className="subtitulo-pedido fonteSubtitulo">{pedido?.nome}</div>
                </div>
              ) : null}
              {pedido?.catalogo?.identificacao ? (
                <div className="column-container">
                  <div className="titulo-pedido fonteTexto">Código do catálogo</div>
                  <a
                    href={`${empresa?.dominio_https}${pedido?.catalogo.identificacao}`}
                    target="_blank"
                    className="subtitulo-pedido fonteSubtitulo"
                  >
                    {pedido?.catalogo.identificacao}
                  </a>
                </div>
              ) : null}
            </div>
          </div>
          )}

          {(pedido?.erp_info && (
            <div className="container-branco">
              <div className="flex-container">
                {pedido?.erp_info ? (
                  <div
                    className="column-container"
                    style={{ height: "auto", width: "100%", overflowWrap: "anywhere" }}
                  >
                    <div className="titulo-pedido fonteTexto">
                      Status sincronização ERP
                    </div>
                    <div className="subtitulo-pedido fonteSubtitulo">{pedido?.erp_info}</div>
                    {pedido?.erp_situacao === -1 && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.8rem",
                          marginTop: "1rem",
                        }}
                      >
                        <Button
                          loading={loadingForcar}
                          className="botao-bottom fonteTexto"
                          padding="5rem"
                          title="Forçar pedido"
                          onClick={() => handleForcarSincronizacao()}
                        />
                        <PopoverInfo
                          mensagem="O pedido será reenfileirado para ser reprocessado."
                          width="15"
                        />
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          )) ||
            null}

          <div className="container-branco">
            <div className="flex-container">
              {pedido?.valor_total ? (
                <div className="column-container">
                  <div className="titulo-pedido fonteTexto">Valor final</div>
                  <div className="subtitulo-pedido fonteSubtitulo">
                    <span>
                        {Number(informacoesPagamento?.juros_valor) > 0 ? '*' : ''}
                        {`${pedido.moeda} ${(Number.parseFloat(
                        informacoesPagamento?.valor_cobranca || "0"
                      ) / 100)
                        .toFixed(2)
                        .replace(".", ",")}`}
                    </span>
                  </div>
                </div>
              ) : null}
              {pedido?.valor_total ? (
                <div className="column-container">
                  <div className="titulo-pedido fonteTexto">Valor dos produtos</div>
                  <div className="subtitulo-pedido fonteSubtitulo">
                    {`${pedido.moeda} ${Number.parseFloat(pedido?.valor_total)
                      .toFixed(2)
                      .replace(".", ",")}`}
                  </div>
                </div>
              ) : null}
              {pedido?.frete?.valor_frete ? (
                <div className="column-container">
                  <div className="titulo-pedido fonteTexto">Valor do Frete</div>
                  <div className="subtitulo-pedido fonteSubtitulo">
                    {`${pedido.moeda} ${Number.parseFloat(
                      pedido?.frete.valor_frete
                    )
                      .toFixed(2)
                      .replace(".", ",")}`}
                  </div>
                </div>
              ) : null}
              {pedido?.desconto_progressivo?.valor && pedido.desconto_progressivo.valor > 0 ? (
                <div className="column-container">
                  <div className="titulo-pedido fonteTexto">
                    Desconto Progressivo | -{pedido.desconto_progressivo.percentual}%
                  </div>
                  <div className="subtitulo-pedido fonteSubtitulo">
                    {`${pedido?.moeda} ${pedido.desconto_progressivo.valor
                      .toFixed(2)
                      .replace(".", ",")}`}
                  </div>
                </div>
              ) : null}
              {pedido?.desconto_cupom && Number(pedido.desconto_cupom) > 0 ? (
                <div className="column-container">
                  <div className="titulo-pedido fonteTexto">Desconto Cupom ({pedido?.tipo_desconto == "porcentagem" ? "%" : "R$"}) | {pedido?.cupom}</div>
                  <div className="subtitulo-pedido fonteSubtitulo">
                    {`${pedido?.moeda} ${(calcularDescontoCupom() / 100)
                      .toFixed(2)
                      .replace(".", ",")}`}
                  </div>
                </div>
              ) : null}
              {informacoesPagamento?.desconto && Number.parseFloat(informacoesPagamento.desconto) > 0 ? (
                <div className="column-container">
                  <div className="titulo-pedido fonteTexto">Desconto do pix</div>
                  <div className="subtitulo-pedido fonteSubtitulo">
                    {`${pedido?.moeda} ${calcularDescontoPix()
                      .toFixed(2)
                      .replace(".", ",")}`}
                  </div>
                </div>
              ) : null}
            </div>
            <div className="linha-menor"></div>
            <div className="flex-container">{switchFormasDePagamento()}</div>
            <div className="linha-menor"></div>
            <div className="flex-container">
              {pedido?.frete ? (
                <>
                  <div className="column-container">
                    <div className="titulo-pedido fonteTexto">Tipo de frete</div>
                    <div className="subtitulo-pedido fonteSubtitulo">
                      {formatarTipoFrete(pedido?.frete.tipo_frete)}
                    </div>
                  </div>
                  {pedido?.frete.id_frete_tipo == "5" && !pedido?.dropshipping?.travado && (
                    <div className="column-container center">
                      <div className="titulo-pedido fonteTexto"></div>
                      <div className="subtitulo-pedido fonteSubtitulo">
                        {pedido?.frete.url_etiqueta ? (
                          <a
                            href={pedido?.frete.url_etiqueta}
                            target="_blank"
                            className="subtitulo-pedido fonteSubtitulo"
                          >
                            <Button
                              title="Imprimir etiqueta"
                              className="botao"
                              fontSize="2.4rem"
                            />
                          </a>
                        ) : (
                          <NotaFiscal
                            pedido={pedido}
                            empresa={empresa!.tipo_pessoa}
                            changeFretes={changeFretes}
                            frete={formatarTipoFrete(pedido?.frete.tipo_frete)}
                          />
                        )}
                      </div>
                    </div>
                  )}
                  {pedido?.frete.tipo_frete !== "a_combinar" &&
                    pedido?.frete.tipo_frete !== "retirada_no_local" && (
                      <div className="column-container">
                        <div className="titulo-pedido fonteTexto">Código de rastreio</div>
                        <div className="subtitulo-pedido fonteSubtitulo">
                          {pedido?.dropshipping?.travado ? (
                            // Dropshipping: o rastreio é informado pelo fornecedor
                            pedido?.frete.codigo_rastreio || "Não informado"
                          ) : (
                            <CodigoRastreio
                              codigo={pedido?.frete.codigo_rastreio}
                              disabled={pedido?.frete.url_etiqueta != ""}
                              id_pedido={pedido.id_pedido || ""}
                            />
                          )}
                        </div>
                      </div>
                    )}
                  <div className="column-container">
                    <div className="titulo-pedido fonteTexto">Prazo de entrega</div>
                    <div className="subtitulo-pedido fonteSubtitulo">
                      {pedido?.frete.prazo_frete && Number(pedido?.frete.prazo_frete) > 0 ? (
                        <>
                          {Number(pedido?.frete.prazo_frete) + Number(pedido?.frete.dias_entrega_adicionais)} dia(s)
                        </>
                      ) : (
                        "Não informado"
                      )}
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          </div>

          <div className="container-branco">
            <TableProdutos
              produtos={pedido?.produtos || []}
              moeda={pedido?.moeda || "R$"}
              template="principal"
            />
          </div>

          <div className="container-branco">
            {pedido?.cliente.observacao && (
              <>
                <div className="flex-container">
                  <div className="column-container">
                    <div className="titulo-pedido fonteSubtitulo" style={{fontWeight: 'bold'}}>Observação do cliente</div>
                    <div className="subtitulo-pedido fonteRotulos">
                      {pedido?.cliente.observacao}
                    </div>
                  </div>
                </div>
                <div className="linha-menor"></div>
              </>
            )}
            {id && (
              <ObservacoesPedido
                idPedido={id}
                observacoes={observacoes}
                onObservacoesChange={setObservacoes}
              />
            )}
          </div>
          <FooterPaginas>
            <div className="flex-botoes-pedido">
              <div className="flex-esquerda">
                <Button
                  loading={loadingPrinting}
                  className="botao-bottom fonteTexto"
                  padding="5rem"
                  title="Imprimir pedido"
                  onClick={() => handlePrint()}
                  onOptionsClick={() => setModalLayoutOpen(true)}
                  optionsTitle="Configurar layout de impressão"
                />
                <Button
                  className="botao-bottom fonteTexto"
                  padding="5rem"
                  title="Salvar em CSV"
                  onClick={handleExportarPedido}
                  onOptionsClick={() => setModalCsvOpen(true)}
                  optionsTitle="Configurar campos do CSV"
                />

                {pedido?.hash && (
                  <Button
                    className="botao-bottom fonteTexto"
                    padding="5rem"
                    title="Link do pedido"
                    onClick={() =>{
                      const link = `${empresa?.dominio_https}${pedido?.catalogo?.identificacao}/detalhes-pedido/${pedido.hash}`;
                      navigator.clipboard.writeText(link).then(() => {
                        Swal.fire({
                          title: "Link copiado!",
                          text: "O link do pedido foi copiado para a área de transferência.",
                          icon: "success",
                          confirmButtonText: "OK",
                        });
                      }).catch(() => {
                        Swal.fire({
                          title: "Erro ao copiar link",
                          text: "Não foi possível copiar o link do pedido. Tente novamente.",
                          icon: "error",
                          confirmButtonText: "OK",
                        });
                      })
                    }}
                  />
                )}
                {pedido?.hash && pedido?.cliente.telefone && (
                  <Button
                    className="botao-bottom fonteTexto"
                    padding="5rem"
                    title="Compartilhar pedido"
                    icon={<WhatsAppIcon />}
                    onClick={() => {
                      const telefone = `55${pedido.cliente.telefone}`;
                      const mensagem = encodeURIComponent(
                        `Confira os detalhes do seu pedido aqui: ${empresa?.dominio_https}detalhes-pedido/${
                          pedido!.hash
                        }`
                      );
                      const whatsappUrl = `https://api.whatsapp.com/send?phone=${telefone}&text=${mensagem}`;

                      window.open(whatsappUrl, "_blank");
                    }}
                  />
                )}
              </div>
              {pedido?.situacao !== "3" && !pedido?.dropshipping?.travado && (
                <Button
                  className="botao-bottom fonteTexto"
                  padding="5rem"
                  title="Cancelar pedido"
                  onClick={handleCancelarPedido}
                />
              )}
            </div>
          </FooterPaginas>
        </ContainerPedido>
      )}
      <PrintablePedido
        pedido={memoizedPedido}
        informacoesPagamento={memoizedInformacoesPagamento}
        ref={printableRef}
        layoutConfig={layoutConfig}
        ordenacao={ordenacaoProdutos}
        observacoes={observacoes}
      />
      <ModalLayoutImpressao
        isOpen={modalLayoutOpen}
        onClose={handleModalClose}
        tipo="pedido"
      />
      <ModalLayoutCsv
        isOpen={modalCsvOpen}
        onClose={() => setModalCsvOpen(false)}
      />
    </>
  );
};

export default Pedido;
